import React from 'react';
import { X, AlertTriangle, Trash2, Ban } from 'lucide-react';
import type { Form } from '../../../services/formService';

interface DeleteFormModalProps {
    isOpen: boolean;
    form: Form | null;
    isDeleting?: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DeleteFormModal: React.FC<DeleteFormModalProps> = ({
    isOpen,
    form,
    isDeleting = false,
    onClose,
    onConfirm
}) => {
    if (!isOpen || !form) return null;

    const isBlocked = !!form.processKey;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isBlocked ? 'bg-orange-100' : 'bg-red-100'}`}>
                            {isBlocked ? (
                                <Ban size={20} className="text-orange-600" />
                            ) : (
                                <AlertTriangle size={20} className="text-red-600" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">
                                {isBlocked ? 'Cannot Delete Form' : 'Delete Form'}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {isBlocked ? 'Action restricted' : 'Confirm deletion'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {isBlocked ? (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600">
                                This form is currently mapped to a process and cannot be deleted.
                            </p>
                            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                                <p className="text-xs font-medium text-orange-800 uppercase mb-1">
                                    Process Key
                                </p>
                                <p className="text-sm text-gray-900 font-mono">
                                    {form.processKey}
                                </p>
                            </div>
                            <p className="text-xs text-gray-500">
                                Please unmap the form from the process definition before attempting to delete it.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600">
                                Are you sure you want to delete this form? This action cannot be undone.
                            </p>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900">
                                    {form.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Version: {form.version || 'N/A'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        {isBlocked ? 'Close' : 'Cancel'}
                    </button>
                    {!isBlocked && (
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isDeleting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} />
                                    Delete Form
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteFormModal;
